/*
 * "Who closed this task, and when" line for terminal rows on the list and form.
 * Done/Skip carry the acting CMS user; system closes carry closed_reason instead.
 */

import moment from 'moment-timezone';
import { label_for } from './outcome';
import type { CodeLabel } from './outcome';

const IST_TZ = 'Asia/Kolkata';
const SKIP_PREFIX = 'skip:';

// Mirrors goserver ClosedReason* constants.
export const CLOSED_REASON_LABELS: Record<string, string> = {
  became_member: 'Became a member',
  account_deleted: 'Account deleted',
  banned: 'Student banned',
  lead_verified: 'Lead verified',
  lead_expired: 'Lead expired',
  instant_expired: 'Instant task expired',
  newer_task_closed: 'Newer task closed',
  session_booked: 'Session booked',
  session_cancelled: 'Session cancelled',
  stop_note: 'Do-not-contact note',
  family_plan: 'Added to a family plan',
  otp_verified_later: 'Signed in after OTP failure',
  plan_opened: 'Plan opened',
  // Written by the 28 Oct duplicate-account cleanup migration, not by goserver.
  duplicate_account: 'Duplicate account'
};

export interface ClosedByItem {
  status?: string | null;
  closed_by_ops_uuid?: string | null;
  closed_by_name?: string | null;
  closed_reason?: string | null;
  closed_at_iso?: string | null;
  skip_reason?: string | null;
}

// "not_interested" -> "Not interested"; the fallback when no configured label exists.
const humanize = (code: string): string => {
  const s = code.replace(/_+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

export const skip_reason_label = (
  code: string | null | undefined,
  skip_reasons: CodeLabel[] = []
): string => {
  if (!code) return '';
  const label = label_for(skip_reasons, code);
  return label === code ? humanize(code) : label;
};

export const closed_reason_label = (
  reason: string | null | undefined,
  skip_reasons: CodeLabel[] = []
): string => {
  if (!reason) return '';
  if (reason.startsWith(SKIP_PREFIX))
    return skip_reason_label(reason.slice(SKIP_PREFIX.length), skip_reasons);
  return CLOSED_REASON_LABELS[reason] || humanize(reason);
};

// "17 Oct, 2:05 PM IST"; the year is added only when it is not the current IST year.
export const format_closed_time = (
  iso: string | null | undefined,
  now: Date = new Date()
): string => {
  if (!iso) return '';
  const m = moment(iso, moment.ISO_8601, true);
  if (!m.isValid()) return '';
  const t = m.tz(IST_TZ);
  const same_year = t.year() === moment(now).tz(IST_TZ).year();
  return `${t.format(same_year ? 'D MMM, h:mm A' : 'D MMM YYYY, h:mm A')} IST`;
};

const actor = (item: ClosedByItem): string =>
  item.closed_by_name || (item.closed_by_ops_uuid ? 'unknown user' : '');

export interface ClosedByOptions {
  skip_reasons?: CodeLabel[];
  // The form already shows the skip reason in its own banner.
  include_skip_reason?: boolean;
  now?: Date;
}

// '' for an open row (or an unknown status).
export const closed_by_line = (
  item: ClosedByItem | null | undefined,
  opts: ClosedByOptions = {}
): string => {
  if (!item) return '';
  const { skip_reasons = [], include_skip_reason = true, now } = opts;
  const who = actor(item);
  const parts: string[] = [];
  switch (item.status) {
    case 'submitted':
      parts.push(who ? `Done by ${who}` : 'Done');
      break;
    case 'skipped': {
      parts.push(who ? `Skipped by ${who}` : 'Skipped');
      if (include_skip_reason) {
        const code = item.skip_reason || (item.closed_reason || '').replace(SKIP_PREFIX, '');
        const label = skip_reason_label(code, skip_reasons);
        if (label) parts.push(label);
      }
      break;
    }
    case 'auto_closed':
    case 'auto_completed': {
      parts.push('Closed automatically');
      const label = closed_reason_label(item.closed_reason, skip_reasons);
      if (label) parts.push(label);
      break;
    }
    default:
      return '';
  }
  const when = format_closed_time(item.closed_at_iso, now);
  if (when) parts.push(when);
  return parts.join(' · ');
};
